// src/ResumenCompra.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ItemCarrito } from './types';
import { COMPANY_INFO } from './config'; // <--- CORRECCIÓN AQUÍ

interface ResumenCompraProps {
    carrito: ItemCarrito[];
    onRemoveFromCart: (id: number) => void;
}

const ResumenCompra: React.FC<ResumenCompraProps> = ({ carrito, onRemoveFromCart }) => {
    
    const navigate = useNavigate();

    // Calcular Total
    const totalGeneral = carrito.reduce((sum, item) => sum + (item.precio * item.cantidad), 0);

    // Generar Mensaje de WhatsApp
    const handleCheckout = () => {
        let mensaje = "Hola Bestune Cars! 👋 Me interesa comprar los siguientes repuestos/accesorios:\n\n";
        
        carrito.forEach(item => {
            mensaje += `🔹 *${item.nombre}*\n   Cant: ${item.cantidad} x $${item.precio} = $${item.cantidad * item.precio}\n`;
        });

        mensaje += `\n💰 *TOTAL A PAGAR: $${totalGeneral.toLocaleString('es-ES')}*`;
        mensaje += "\n\nQuedo atento para coordinar el pago y envío.";

        // CORRECCIÓN: Usamos COMPANY_INFO en lugar de CONTACT_INFO
        const url = `https://wa.me/${COMPANY_INFO.whatsappNumber}?text=${encodeURIComponent(mensaje)}`;
        window.open(url, '_blank');
    };

    if (carrito.length === 0) {
        return (
            <div className="min-h-screen pt-32 pb-16 flex flex-col items-center justify-center bg-gray-50 px-4">
                <div className="text-center">
                    <div className="bg-gray-200 p-6 inline-block mb-4">
                        <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"></path></svg>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Tu carrito está vacío</h2>
                    <p className="text-gray-500 mb-8">Parece que aún no has agregado repuestos o accesorios.</p>
                    
                    <button 
                        onClick={() => navigate('/catalogo')} 
                        className="bg-yellow-600 text-white font-bold py-3 px-8 hover:bg-yellow-700 transition shadow-lg"
                    >
                        Volver al Catálogo
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-28 pb-16 bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
                
                {/* Encabezado */}
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">Resumen de Pedido</h1>
                    
                    <button 
                        onClick={() => navigate('/catalogo')} 
                        className="text-gray-500 hover:text-black font-medium flex items-center"
                    >
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
                        Seguir Comprando
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    {/* Lista de Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {carrito.map((item) => (
                            <div key={item.id} className="bg-white p-4 shadow-sm border border-gray-100 flex gap-4 items-center">
                                {/* Imagen */}
                                <div className="w-20 h-20 flex-shrink-0 bg-gray-100 overflow-hidden">
                                    <img src={item.imgUrl} alt={item.nombre} className="w-full h-full object-cover" />
                                </div>
                                
                                {/* Info */}
                                <div className="flex-1">
                                    <h3 className="font-bold text-gray-900">{item.nombre}</h3>
                                    <p className="text-sm text-gray-500 capitalize">{item.tipo}</p>
                                    <p className="text-yellow-600 font-bold mt-1">${item.precio.toLocaleString('es-ES')} x {item.cantidad}</p>
                                </div>

                                {/* Subtotal y Eliminar */}
                                <div className="text-right">
                                    <p className="font-bold text-lg text-black mb-2">${(item.precio * item.cantidad).toLocaleString('es-ES')}</p>
                                    <button 
                                        onClick={() => onRemoveFromCart(item.id)}
                                        className="text-red-500 hover:text-red-700 text-sm font-medium underline"
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Resumen Total */}
                    <div className="lg:col-span-1">
                        <div className="bg-white p-6 shadow-lg border border-gray-200 sticky top-28">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">Detalle de Costos</h3>
                            
                            <div className="flex justify-between mb-4 text-gray-600">
                                <span>Subtotal</span>
                                <span>${totalGeneral.toLocaleString('es-ES')}</span>
                            </div>
                            <div className="flex justify-between mb-6 text-gray-600">
                                <span>Envío</span>
                                <span className="text-xs italic">(A coordinar)</span>
                            </div>
                            
                            <div className="border-t border-gray-200 pt-4 mb-8 flex justify-between items-center">
                                <span className="text-xl font-bold text-black">Total</span>
                                <span className="text-2xl font-extrabold text-yellow-600">${totalGeneral.toLocaleString('es-ES')}</span>
                            </div>

                            <button 
                                onClick={handleCheckout}
                                className="w-full bg-green-600 text-white font-bold py-4 rounded-xl hover:bg-green-700 transition duration-200 shadow-lg flex items-center justify-center space-x-3 animate-pulse"
                            >
                                <img src="/WhatsappLogo.webp" alt="WhatsApp Logo" className="w-8 h-8" />
                                <span> Cotizar</span>
                            </button>
                            <p className="text-xs text-center text-gray-400 mt-4">
                                Al hacer clic, se abrirá WhatsApp con el detalle de tu pedido listo para enviar.
                            </p>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ResumenCompra;